import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowRight, FaBoxOpen, FaWhatsapp, FaPhoneAlt, FaLock } from 'react-icons/fa';
import { getDeviceId, getOtpToken, saveOtpToken, clearOtpToken } from '../utils/attribution';
import { normalizePhone, isValidPhone, dialPrefix } from '../utils/phone';

const COUNTRY = 'Benin';

// Lot 6 — "Mes expéditions" (sans compte).
// Hybride : vue immédiate de l'appareil (device_id) + accès par numéro (multi-appareils)
// protégé par un code OTP (sinon fuite de l'historique d'autrui).
const MesExpeditions = () => {
  const apiBase = process.env.REACT_APP_BUSINESS_API_ENDPOINT;
  const prefix = dialPrefix(COUNTRY);

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [unlockedPhone, setUnlockedPhone] = useState('');

  // Déblocage par numéro
  const [showUnlock, setShowUnlock] = useState(false);
  const [otpStep, setOtpStep] = useState('phone'); // 'phone' | 'code'
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [otpError, setOtpError] = useState('');

  const mergeLeads = (a, b) => {
    const map = new Map();
    [...a, ...b].forEach((l) => map.set(l.id, l));
    return Array.from(map.values()).sort((x, y) => y.id - x.id);
  };

  const loadDevice = useCallback(async () => {
    try {
      const deviceId = getDeviceId();
      if (!apiBase || !deviceId) return [];
      const res = await fetch(`${apiBase}/v1/public/quote/history?device_id=${encodeURIComponent(deviceId)}`);
      const data = await res.json().catch(() => null);
      return (data && data.data) || [];
    } catch (_) {
      return [];
    }
  }, [apiBase]);

  const loadPhone = useCallback(async (p, token) => {
    try {
      const res = await fetch(`${apiBase}/v1/public/quote/history?phone=${encodeURIComponent(p)}&token=${encodeURIComponent(token)}`);
      if (res.status === 401) {
        clearOtpToken();
        return { ok: false };
      }
      const data = await res.json().catch(() => null);
      return { ok: res.ok, data: (data && data.data) || [] };
    } catch (_) {
      return { ok: false };
    }
  }, [apiBase]);

  // Chargement initial : appareil + (si jeton en session) numéro.
  useEffect(() => {
    window.scrollTo(0, 0);
    (async () => {
      const deviceLeads = await loadDevice();
      let all = deviceLeads;
      const saved = getOtpToken();
      if (saved && saved.phone && saved.token) {
        const r = await loadPhone(saved.phone, saved.token);
        if (r.ok) {
          all = mergeLeads(all, r.data);
          setUnlockedPhone(saved.phone);
        }
      }
      setLeads(all);
      setLoading(false);
    })();
  }, [loadDevice, loadPhone]);

  const sendCode = async () => {
    setOtpError('');
    if (!isValidPhone(phone, COUNTRY)) {
      setOtpError('Numéro invalide. Ex : 0196123456');
      return;
    }
    setSending(true);
    try {
      const p = normalizePhone(phone, COUNTRY);
      const res = await fetch(`${apiBase}/v1/public/otp/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: p, force: true }),
      });
      const d = await res.json().catch(() => null);
      if (res.ok && d && d.sent) {
        setOtpStep('code');
      } else {
        setOtpError((d && d.message) || 'Envoi du code impossible.');
      }
    } catch (_) {
      setOtpError('Erreur réseau. Réessayez.');
    } finally {
      setSending(false);
    }
  };

  const verifyCode = async () => {
    setOtpError('');
    if (!/^[0-9]{4}$/.test(code.trim())) {
      setOtpError('Entrez le code à 4 chiffres reçu par WhatsApp.');
      return;
    }
    setVerifying(true);
    try {
      const p = normalizePhone(phone, COUNTRY);
      const res = await fetch(`${apiBase}/v1/public/otp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: p, code: code.trim() }),
      });
      const d = await res.json().catch(() => null);
      if (d && d.verified && d.token) {
        saveOtpToken(d.phone || p, d.token);
        const r = await loadPhone(d.phone || p, d.token);
        if (r.ok) setLeads((cur) => mergeLeads(cur, r.data));
        setUnlockedPhone(d.phone || p);
        setShowUnlock(false);
        setOtpStep('phone');
        setCode('');
        setPhone('');
      } else {
        const reason = d && d.reason;
        if (reason === 'expired') setOtpError('Code expiré. Redemandez-en un.');
        else if (reason === 'locked') setOtpError('Trop d\'essais. Redemandez un code.');
        else setOtpError(`Code incorrect${d && typeof d.remaining === 'number' ? ` (${d.remaining} essai(s) restant(s))` : ''}.`);
      }
    } catch (_) {
      setOtpError('Erreur réseau. Réessayez.');
    } finally {
      setVerifying(false);
    }
  };

  const changeNumber = () => {
    clearOtpToken();
    setUnlockedPhone('');
    setShowUnlock(true);
    setOtpStep('phone');
    setPhone('');
    setCode('');
    setOtpError('');
  };

  const statusLabel = (l) => {
    if (l.converted) return { text: 'Commande passée', cls: 'bg-green-100 text-green-700' };
    if (l.needsConfirmation) return { text: 'À confirmer', cls: 'bg-amber-100 text-amber-700' };
    return { text: 'Devis', cls: 'bg-gray-100 text-gray-600' };
  };

  const fmtDate = (ts) => {
    if (!ts) return '';
    try {
      return new Date(ts).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch (_) {
      return '';
    }
  };

  const inputCls =
    'w-full bg-gray-100 rounded-lg py-3 pr-4 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-black';

  const renderUnlock = () => (
    <div className="rounded-2xl border border-gray-200 p-4 sm:p-5 bg-gray-50">
      <div className="flex items-center gap-2 mb-1">
        <FaLock className="w-3.5 h-3.5 text-gray-500" />
        <p className="font-semibold text-gray-900">Retrouver mes expéditions par numéro</p>
      </div>
      <p className="text-sm text-gray-600 mb-3">
        Pour voir vos expéditions depuis un autre appareil, on vous envoie un code par WhatsApp.
      </p>

      {otpStep === 'phone' ? (
        <div className="space-y-2">
          <div className="relative">
            <FaPhoneAlt className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <span className="absolute left-11 top-1/2 -translate-y-1/2 text-sm text-gray-500 font-medium pointer-events-none">{prefix}</span>
            <input
              type="tel"
              value={phone}
              onChange={(e) => { setPhone(e.target.value.replace(/[^\d\s]/g, '')); setOtpError(''); }}
              placeholder="Votre numéro"
              className={`${inputCls} pl-[5.25rem]`}
            />
          </div>
          {otpError && <p className="text-sm text-red-600">{otpError}</p>}
          <button
            type="button"
            onClick={sendCode}
            disabled={sending}
            className="w-full px-6 py-3 rounded-lg font-medium bg-black text-white hover:bg-gray-800 disabled:bg-gray-300"
          >
            {sending ? 'Envoi…' : 'Recevoir le code'}
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          <input
            type="tel"
            inputMode="numeric"
            maxLength={4}
            value={code}
            onChange={(e) => { setCode(e.target.value.replace(/\D/g, '').slice(0, 4)); setOtpError(''); }}
            placeholder="1234"
            className="w-full text-center tracking-[0.5em] text-2xl font-bold bg-gray-100 rounded-lg py-3 focus:outline-none focus:ring-2 focus:ring-black"
          />
          {otpError && <p className="text-sm text-red-600">{otpError}</p>}
          <button
            type="button"
            onClick={verifyCode}
            disabled={verifying}
            className="w-full px-6 py-3 rounded-lg font-medium bg-black text-white hover:bg-gray-800 disabled:bg-gray-300"
          >
            {verifying ? 'Vérification…' : 'Voir mes expéditions'}
          </button>
          <button type="button" onClick={sendCode} disabled={sending} className="w-full text-sm text-gray-600 underline underline-offset-4 disabled:text-gray-300">
            {sending ? 'Envoi…' : 'Renvoyer le code'}
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      <div className="container mx-auto px-4 py-12 max-w-3xl">
        <div className="space-y-2 mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900">Mes expéditions</h1>
          <p className="text-gray-600">Retrouvez vos demandes de livraison et leur suivi, sans créer de compte.</p>
          {unlockedPhone && (
            <p className="text-sm text-gray-500">
              Numéro : <span className="font-medium">{unlockedPhone}</span>{' ('}
              <button type="button" onClick={changeNumber} className="underline underline-offset-2 hover:text-gray-900">changer</button>{')'}
            </p>
          )}
        </div>

        {loading ? (
          <div className="space-y-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-24 animate-pulse rounded-2xl bg-gray-100" />
            ))}
          </div>
        ) : error ? (
          <div className="p-4 rounded-lg bg-red-50 text-red-700 text-sm border border-red-100">{error}</div>
        ) : (
          <div className="space-y-3">
            {leads.length === 0 ? (
              <div className="text-center py-12">
                <FaBoxOpen className="w-14 h-14 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600 mb-6">Aucune expédition sur cet appareil.</p>
                <Link
                  to="/quick-delivery"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium bg-black text-white hover:bg-gray-800 transition-colors"
                >
                  Expédier un colis <FaArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ) : (
              leads.map((l) => {
                const st = statusLabel(l);
                return (
                  <div key={l.id} className="rounded-2xl border border-gray-200 p-4 sm:p-5 hover:shadow-sm transition">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${st.cls}`}>{st.text}</span>
                          <span className="text-xs text-gray-400">{fmtDate(l.timestamp)}</span>
                        </div>
                        <div className="mt-2 text-sm text-gray-900">
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-black shrink-0" />
                            <span className="truncate">{l.pickupCity || l.pickupAddress || 'Récupération'}</span>
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="w-2 h-2 bg-black shrink-0" />
                            <span className="truncate">{l.deliveryCity || l.deliveryAddress || 'Livraison'}</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        {l.tariff > 0 && (
                          <>
                            {l.tariffSource === 'MERCHANT' && (
                              <div className="text-[10px] uppercase tracking-wide text-gray-400">Votre prix</div>
                            )}
                            <div className="font-bold text-gray-900">{Math.round(l.tariff)} F</div>
                            {l.tariffSource === 'MERCHANT' && l.platformTariff > 0 && Math.round(l.platformTariff) !== Math.round(l.tariff) && (
                              <div className="text-[11px] text-gray-400">Plateforme : {Math.round(l.platformTariff)} F</div>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {l.converted && l.trackingId && (
                      <div className="mt-3">
                        <Link
                          to={`/tracking?tracking-id=${encodeURIComponent(l.trackingId)}&submit=1`}
                          className="inline-flex items-center gap-2 text-sm font-medium text-gray-900 underline underline-offset-4 hover:text-black"
                        >
                          Suivre ma commande <FaArrowRight className="w-3.5 h-3.5" />
                        </Link>
                      </div>
                    )}
                  </div>
                );
              })
            )}

            {/* Accès par numéro (multi-appareils) */}
            {!unlockedPhone && (
              showUnlock ? (
                renderUnlock()
              ) : (
                <button
                  type="button"
                  onClick={() => setShowUnlock(true)}
                  className="w-full rounded-2xl border border-dashed border-gray-300 p-4 text-sm font-medium text-gray-700 hover:border-gray-400 hover:text-gray-900 inline-flex items-center justify-center gap-2"
                >
                  <FaLock className="w-3.5 h-3.5" /> Voir mes expéditions depuis un autre appareil
                </button>
              )
            )}
          </div>
        )}

        <div className="mt-10 text-center">
          <a
            href="https://wa.me/22945387945"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm font-medium text-green-600 hover:text-green-700"
          >
            <FaWhatsapp className="w-4 h-4" /> Besoin d'aide ? Écrivez au support
          </a>
        </div>
      </div>
    </div>
  );
};

export default MesExpeditions;
